import {
  MultiMatchQuery,
  NumericRangeFilter,
  RangeFacet,
  RefinementSelectFacet,
  SearchkitConfig,
} from "@searchkit/sdk";
import {
  FacetsList,
  SearchBar,
  ResetSearchButton,
  SelectedFilters,
  Pagination,
  SortingSelector,
} from "@searchkit/elastic-ui";
import { useSearchkitVariables } from "@searchkit/client";
import { useSearchkitSDK } from "@searchkit/sdk/lib/esm/react-hooks";
import {
  EuiPage,
  EuiFlexGrid,
  EuiPageBody,
  EuiPageContent,
  EuiPageContentBody,
  EuiPageHeader,
  EuiPageHeaderSection,
  EuiPageSideBar,
  EuiHorizontalRule,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiFormLabel,
  EuiCheckbox,
} from "@elastic/eui";

import "@elastic/eui/dist/eui_theme_light.css";
import { SearchkitHit } from "@searchkit/sdk/lib/esm";
import MapGl, { Viewport } from "./maps/MapGL";

import { useCallback, useEffect, useMemo, useState } from "react";
import { HitCard } from "./Card";
import { SolarHeader } from "./SolarHeader";
import { SuitabilityLegend } from "./SuitabilityLegend";
import debounce from "lodash/debounce";
import ExistFilter from "./ExistFilter";

import { useTranslation } from "react-i18next";
import { CityPicker } from "./CityPicker";
const defaultConfig: SearchkitConfig = {
  host: "/esearch",
  index: "roofs-index3",
  hits: {
    fields: [
      "city",
      "roof_type",
      "suitabilit",
      "usable_are",
      "lat",
      "lng",
      "sellerId",
      "id",
      "note",
    ],
  },
  sortOptions: [
    { id: "usable_are", label: "Površina", field: { usable_are: "desc" } },
    {
      id: "suitabilit",
      label: "Primernost",
      field: { "suitabilit.keyword": "desc" },
      defaultOption: true,
    },
  ],
  query: new MultiMatchQuery({
    fields: [
      "city",
      "roof_type",
      "suitabilit",
      "writers",
      "actors",
      "countries",
      "plot",
    ],
  }),
  filters: [
    new NumericRangeFilter({
      identifier: "lng",
      field: "lng",
      label: "lng",
      display: "RangeSliderFacet",
    }),
    new NumericRangeFilter({
      identifier: "lat",
      field: "lat",
      label: "lat",
      display: "RangeSliderFacet",
    }),
    new ExistFilter({
      identifier: "sellerId",
      field: "sellerId",
      label: "Claimed",
      display: "ListFacet",
    }),
  ],
  facets: [
    new RangeFacet({
      field: "usable_are",
      identifier: "usable_are",
      label: "Površina",
      range: {
        min: 0,
        min_doc_count: 0,
        max: 100,
        interval: 1,
      },
    }),

    new RefinementSelectFacet({
      field: "suitabilit.keyword",
      identifier: "suitabilit",
      label: "Osunčanost",
      order: "value",
    }),
  ],
};

const HitsList = ({ items }: { items: SearchkitHit[] }) => (
  <div>
    <EuiSpacer size="l" />

    <EuiFlexGrid columns={4}>
      {items.map((hit) => (
        <EuiFlexItem key={hit.id}>
          <HitCard hit={hit} />
        </EuiFlexItem>
      ))}
    </EuiFlexGrid>
    <EuiSpacer size="l" />
  </div>
);
export function setMapFocus(hit: SearchkitHit) {}

function App(): JSX.Element {
  const Facets = FacetsList([]);
  const [bbox, setBbox] = useState<number[] | undefined>([
    45.548, 13.74, 45.546, 13.72,
  ]);
  const [viewport, setViewport] = useState<Viewport>({
    latitude: 45.547,
    longitude: 13.73,
    zoom: 15,
    bearing: 0,
    pitch: 0,
  });
  const [claimedOnly, setClaimedOnly] = useState(false);

  let variables = useSearchkitVariables();
  variables.page.size = 100;

  variables = useMemo(() => {
    if (bbox?.length === 4) {
      return {
        ...variables,
        filters: [
          ...variables.filters,
          { identifier: "lat", min: bbox[2], max: bbox[0] },
          { identifier: "lng", min: bbox[3], max: bbox[1] },
          claimedOnly
            ? { identifier: "sellerId", value: claimedOnly.toString() }
            : null,
        ].filter((x) => x) as any,
      };
    }
    return variables;
  }, [bbox, variables, claimedOnly]);
  const { results, loading } = useSearchkitSDK(defaultConfig as any, variables);
  const onSearchAreaClicked = useCallback(() => {
    setBbox(viewport?.bbox);
  }, [viewport]);

  const onCityChanged = useCallback(() => {
    setBbox(viewport?.bbox);
  }, [setBbox, viewport]);

  const debouncedSearch = useMemo(
    () =>
      debounce((viewport: Viewport) => {
        viewport?.bbox && setBbox(viewport?.bbox);
      }, 600),
    [setBbox]
  );

  useEffect(() => {
    debouncedSearch(viewport);
  }, [viewport]);
  const { t } = useTranslation();

  return (
    <>
      <SolarHeader />
      <EuiPage>
        <EuiPageSideBar>
          <SearchBar loading={loading} />
          <EuiHorizontalRule margin="m" />

          <CityPicker onChanged={onCityChanged} viewport={viewport} />

          <Facets data={results} loading={loading} />
          <EuiCheckbox
            label="Only claimed"
            checked={claimedOnly}
            onChange={() => setClaimedOnly(!claimedOnly)}
            id="claimedOnly"
          />

          <SuitabilityLegend />
        </EuiPageSideBar>
        <EuiPageBody component="div">
          <EuiPageHeader>
            <EuiPageHeaderSection>
              <SelectedFilters
                data={results}
                loading={loading}
                customFilterComponents={{ empty: <span></span> }}
              />
              <EuiFlexGroup
                alignItems="center"
                style={{ padding: "15px 10px 10px 10px" }}
              >
                <EuiFormLabel style={{ paddingRight: "10px" }}>
                  {t("Sort by")}:
                </EuiFormLabel>
                <SortingSelector data={results} loading={loading} />
              </EuiFlexGroup>
            </EuiPageHeaderSection>
            <EuiPageHeaderSection>
              <ResetSearchButton loading={loading} />
            </EuiPageHeaderSection>
          </EuiPageHeader>
          <EuiPageContent>
            <EuiPageContentBody>
              <MapGl
                onSearchAreaClicked={onSearchAreaClicked}
                onSetViewport={setViewport}
                hits={results?.hits.items || []}
                accessToken="pk.eyJ1IjoicmljaGhvb2RpZSIsImEiOiJja3ZndzlhMmQxdnE4MnVxaTFjcXNrdTdmIn0.eNMjE6h0bdJLR3J5QTYbTg"
                viewport={viewport}
              />
              <br />
              <br />
              <EuiFlexGroup justifyContent="spaceBetween">
                <Pagination data={results} />
                <span>
                  {results?.summary.total} {t("results")}
                </span>
              </EuiFlexGroup>

              <HitsList items={results?.hits.items || []} />
              <EuiFlexGroup justifyContent="spaceAround">
                <Pagination data={results} />
              </EuiFlexGroup>
            </EuiPageContentBody>
          </EuiPageContent>
        </EuiPageBody>
      </EuiPage>
    </>
  );
}

export default App;
